import { useLayoutEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PinLock } from "common-fe";
import TopBar from "./TopBar";
import SessionHeader from "./SessionHeader";
import RebuildLocalDataButton from "./RebuildLocalDataButton";
import { useScoreContext } from "../context/ScoreContext";
import { getScribePin } from "../util/scribePin";

// Composes the persistent chrome (brand AppBar + Session strip) above
// the routed page content. Pages render their own breadcrumbs inside
// children since the crumb trail varies per screen.
const APP_BAR_HEIGHT = 64;

// #1665 — SessionHeader's own chip row can wrap to a second line whenever its
// content grows wide enough (position/judge/sequence chips can do this at
// narrow widths — #1701 makes judge/sequence collapse below `md` instead,
// but the mechanism here still has to hold for whatever wraps regardless).
// A sticky child below it (ScoringForm's "scoring-subheader",
// ClassList's sticky filter bar) can't know that height at author time, so it
// used to hardcode a single-line-assuming pixel offset (top:105 = AppBar 64 +
// SessionHeader py 16 + one chip row 24 + border 1) that silently went stale
// the moment SessionHeader grew a second row — exactly what the testName chip
// did, covering the CNO/Rider/Horse row (score-page-ui.cy.js, class-fault-
// time-limit.cy.js, both real regressions this card caused, proven via a
// call-site probe: neutering the testName fallback made both green again).
// Fixed at the root instead of re-guessing a new magic number: this measures
// SessionHeader's ACTUAL rendered height (ResizeObserver, so it tracks wrap
// changes live) and publishes it as a CSS custom property on the document
// root, with the old 105px as the var() fallback for the one frame before the
// effect runs. Consumers replace their hardcoded top with
// `var(--fv-session-header-bottom, 105px)`.
const SESSION_HEADER_BOTTOM_VAR = "--fv-session-header-bottom";

export default function AppShell({
  children,
  sessionSubtitle = null,
  sessionNav = null,
  sessionJudge = null,
  sessionPosition = null,
  sessionSequence = null,
  // #636/#647 — the class currently in view, so SessionHeader can show its
  // next ride. null on screens with no single class in context (e.g. ClassList).
  classId = null,
  // #1557 — the classes screen has no single class, so it opts into a next-CLASS
  // chip instead of the next-ride one. See SessionHeader for the tri-state.
  showNextClass = false,
  nextClass = null,
  nextClassTime = null,
  // #2196 — passthrough for SessionHeader's next-class loading chip.
  nextClassLoading = false,
}) {
  const navigate = useNavigate();
  const { unlockPicker, selectedShow } = useScoreContext();
  const sessionHeaderRef = useRef(null);

  // #628 — the lock lives on every scribe screen (it's the only way to change
  // org/show since #627 dropped the menu item). Unlocking opens the pickers on
  // /setup, wherever the scribe happened to be.
  const handleUnlock = () => {
    unlockPicker();
    navigate("/setup");
  };

  // #1665 — see SESSION_HEADER_BOTTOM_VAR above. Runs on every render (not
  // just mount) since sessionNav/sessionJudge/etc. can change the chip
  // row's wrap without SessionHeader's own size changing in a way ResizeObserver
  // alone would catch fast enough for the very first paint after a route
  // change; the observer still covers later async-data-driven wraps (e.g. a
  // next-ride chip's label arriving after the initial mount).
  useLayoutEffect(() => {
    const el = sessionHeaderRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const publish = () => {
      const height = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        SESSION_HEADER_BOTTOM_VAR,
        `${APP_BAR_HEIGHT + height}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  });

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <TopBar />
      <PinLock
        pin={getScribePin(selectedShow)}
        onUnlock={handleUnlock}
        testId="scribe-lock"
      />
      {/* #1902 — support-only recovery, mounted alongside PinLock (not
          inside it) so it survives card #2112's concurrent PinLock work. */}
      <RebuildLocalDataButton />
      <Box sx={{ pt: `${APP_BAR_HEIGHT}px` }}>
        <SessionHeader
          ref={sessionHeaderRef}
          subtitle={sessionSubtitle}
          nav={sessionNav}
          judge={sessionJudge}
          position={sessionPosition}
          sequence={sessionSequence}
          classId={classId}
          showNextClass={showNextClass}
          nextClass={nextClass}
          nextClassTime={nextClassTime}
          nextClassLoading={nextClassLoading}
        />
        <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1080, mx: "auto" }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
